import styled from "styled-components";
import { useEffect, useRef, useState } from "react";
import ExpandableSection from "./ExpandableSection";
import { accentColor } from "./style";
import FullScreenContainer from "./FullScreenContainer";
import LoopCycleImage from "../img/figures/cycle-with-text.png";
import { breakpoint } from "./style";


const LoopSectionContainer = styled.section`
    position: relative;
    display: flex;
    flex-direction: row;
    width: 100%;
    margin-bottom: 400px;

    @media ${breakpoint.laptop} {
        flex-direction: column;
        margin-bottom: 100px;
    }
`;

const LoopStickySection = styled.div`
    position: -moz-sticky;
    position: -ms-sticky;
    position: -o-sticky;
    position: sticky;
    top: 0;
    align-self: flex-start;
    height: 100vh;
    flex: 1 0 40%;
    width: 40%;
    align-items: center;
    display: flex;
    overflow: hidden;

    &:before {
        position: absolute;
        top: 0px;
        left: 0px;
        z-index: 100;
        width: 60%;
        height: 100vh;
        content: "";
        background: linear-gradient(to right, rgba(255,255,255,1), rgba(255,255,255,0));
    }

    @media ${breakpoint.laptop} {
        flex: unset;
        z-index: -100;

    }

    @media ${breakpoint.phone} {
        width: 100%;


    }


`;



const LoopContentSection = styled.div`
    flex: 1 0 60%;
    width: 60%;

    & h3 {
        font-size: 20pt;
        color: ${accentColor};
    }

    @media ${breakpoint.laptop} {
        width: 80%;
        flex: unset;
        z-index: 100;
        height: fit-content;
        margin-top: -50vh;
    }

    @media ${breakpoint.phone} {
        width: 100%;
    }

`;

const rotationAdjustment = -5;
const rotationMap = new Map([
    ["movement", 60 + rotationAdjustment],
    ["sensors", 0 + rotationAdjustment],
    ["data", -60 + rotationAdjustment],
    ["visualization", -120 + rotationAdjustment],
    ["interpretation", -180 + rotationAdjustment],
    ["sonification", -240 + rotationAdjustment]
]);



const LoopImage = styled.img`
    width: 150%;
    transition: transform 1s ease-in-out;
    margin-left: -60%;

    &.movement {
        transform: rotate(${rotationMap.get("movement")}deg);
    }

    &.sensors {
        transform: rotate(${rotationMap.get("sensors")}deg);
    }

    &.data {
        transform: rotate(${rotationMap.get("data")}deg);
    }

    &.visualization {
        transform: rotate(${rotationMap.get("visualization")}deg);
    }

    &.interpretation {
        transform: rotate(${rotationMap.get("interpretation")}deg);
    }

    &.sonification {
        transform: rotate(${rotationMap.get("sonification")}deg);
    }


    `;

const ObserverSection = styled.div`
    margin: 0px;
    padding: 0px;
`;

export const LoopSectionName = Object.freeze({
    Movement: "movement",
    Sensors: "sensors",
    Data: "data",
    Visualization: "visualization",
    Interpretation: "interpretation",
    Sonification: "sonification"
});

const LoopSection = ({setSectionIsExpanded}) => {

    const [loopSection, setLoopSection] = useState(LoopSectionName.Movement);

    const [showFullScreenContainer, setShowFullScreenContainer] = useState(false);
    const [whichSectionToView, setWhichSectionToView] = useState(LoopSectionName.Movement);


    const loopImage = useRef(null);

    const section_movement = useRef(null);
    const section_sensors = useRef(null);
    const section_data = useRef(null);
    const section_visualization = useRef(null);
    const section_interpretation = useRef(null);
    const section_sonification = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver((entries, observer) => {
            for(let entry of entries) {
                if(entry.isIntersecting) {
                    const thisLoopSection = entry.target.getAttribute("data-section");
                    if(loopSection && loopSection !== thisLoopSection) {
                        setLoopSection(thisLoopSection);
                        break;
                    }

                    // Default action if no intersection entry triggered
                    setLoopSection(LoopSectionName.Movement);
                }
            }

        }, {rootMargin: "0px 0px 0px 0px", threshold: 0.5});

        observer.observe(section_movement.current);
        observer.observe(section_sensors.current);
        observer.observe(section_data.current);
        observer.observe(section_visualization.current);
        observer.observe(section_interpretation.current);
        observer.observe(section_sonification.current);

    }, []);

  


    return <>
            <FullScreenContainer showFullScreenContainer={showFullScreenContainer} setWhichSectionToView={setWhichSectionToView} whichSectionToView={whichSectionToView} setShowFullScreenContainer={setShowFullScreenContainer} setSectionIsExpanded={setSectionIsExpanded} />
    <LoopSectionContainer>
        <LoopStickySection>
            <LoopImage className={loopSection} ref={loopImage} src={LoopCycleImage}/>
        </LoopStickySection>
        <LoopContentSection>
            <ObserverSection ref={section_movement} data-section={LoopSectionName.Movement}>
                <ExpandableSection 
                    handleReadMore={() => {
                        setWhichSectionToView(LoopSectionName.Movement);
                        setSectionIsExpanded(true);
                        setShowFullScreenContainer(true);
                    }}
                >
                <p>
                <h3>Movement</h3>
                The production in its current form begins with a duet between dancers, the movement between whom becomes the entry point and foundational material for the looping structure that is set into motion. This movement consists of effortful action, drawing from contemporary dance forms including Forsythe, Horton, Release, and Latinx. Ordered around explicit attention to the dynamic equilibrium that is continuously negotiated between the dancers, the movement explores changes in direction, position, orientation, and speed through the realization of potential energy into kinetic energy. Through this, the movement initiates the exploration of many kinds of relationships: to self, environment (the floor, the sound produced by the musicians), and other (partner).
                </p>

                </ExpandableSection>

            </ObserverSection>
            <ObserverSection ref={section_sensors} data-section={LoopSectionName.Sensors}>
            <ExpandableSection 
                    handleReadMore={() => {
                        setWhichSectionToView(LoopSectionName.Sensors);
                        setSectionIsExpanded(true);
                        setShowFullScreenContainer(true);
                    }}
            >
                <p>
                <h3>Sensors</h3>
                As the dancers move, the kinesthetic information exchanged between them is captured in real time by wearables worn by the dancers. These include sets of gloves and insoles — two gloves (left and right hand) and two insoles (left and right foot) — worn by both dancers. Inside the wearables, sensors are embedded that capture changes in pressure, ranging from subtle to significant, based on piezoelectric conversions of observed pressure into electrical charges.</p>

                </ExpandableSection>


            </ObserverSection>
            <ObserverSection ref={section_data} data-section={LoopSectionName.Data}>
            <ExpandableSection 
                    handleReadMore={() => {
                        setWhichSectionToView(LoopSectionName.Data);
                        setSectionIsExpanded(true);
                        setShowFullScreenContainer(true);
                    }}
            >
                <p>
                <h3>Data</h3>
                The sensors send a signal from transmitters to a receiver, which is connected to a computer. The computer then translates these incoming signals into a data series that can be read and used as a measurement of changes in pressure. Specifically, the absolute magnitude of pressure on each sensor is mapped to an 8-bit value between 0 and 127. When the value is 0, no pressure is detected on a sensor; when the value is 127, the maximum possible pressure intensity is detected on the sensor. Since each sensor varies in its sensitivity, as described above, this translation of each signal into this standardized range of values from 0 to 127 makes it possible to calibrate the sensors against one another, enabling comparisons across them.</p>

                </ExpandableSection>



            </ObserverSection>
            <ObserverSection ref={section_visualization} data-section={LoopSectionName.Visualization}>
            <ExpandableSection 
                    handleReadMore={() => {
                        setWhichSectionToView(LoopSectionName.Visualization);
                        setSectionIsExpanded(true);
                        setShowFullScreenContainer(true);
                    }}
            >
                <p>
                <h3>Visualization</h3>
                The underlying purpose of the sensors is to provide an interface through which invisible dimensions of partnered interaction might be captured. Once captured, these dimensions were to be rendered in a way that makes them visible to others. In other words, the sensors serve as a technology to make the invisible visible, and the medium through which the rendering occurred was data visualization.</p>

                </ExpandableSection>


            </ObserverSection>
            <ObserverSection ref={section_interpretation} data-section={LoopSectionName.Interpretation}>
            <ExpandableSection 
                    handleReadMore={() => {
                        setWhichSectionToView(LoopSectionName.Interpretation);
                        setSectionIsExpanded(true);
                        setShowFullScreenContainer(true);
                    }}
            >
                <p>
                <h3>Interpretation</h3>
                The visualization described above is displayed on the screen of a computer which continuously listens for changes in data sent by the sensors. This screen is then placed in front of a pair of musicians. As the musicians play, they are asked to read and respond to the visualization as it changes over time, incorporating their interpretation into the way they play their instruments. Of course, this raises an essential question: How exactly should the musicians read the visualization and interpret meaning from it?</p>

                </ExpandableSection>



            </ObserverSection>
            <ObserverSection ref={section_sonification} data-section={LoopSectionName.Sonification}>
            <ExpandableSection 
                    handleReadMore={() => {
                        setWhichSectionToView(LoopSectionName.Sonification);
                        setSectionIsExpanded(true);
                        setShowFullScreenContainer(true);
                    }}
            >
                <p>
                <h3>Sonification</h3>
                In the final portion of the loop, the musicians use their interpretation of the visualization to generate sound with their instruments. This is a process of sonification: having begun in the movement of the dancers, the kinesthetic phenomena that are captured by sensors, translated into a visualization, and then interpreted by musicians are finally turned into sound, completing the loop.</p>

                </ExpandableSection>



            </ObserverSection>
        </LoopContentSection>
    </LoopSectionContainer>
    </>;

}

export default LoopSection;